import { useEffect, useState } from "react";
import { brand, navLinks } from "@/mocks/chigua";

interface NavbarProps {
  onSubscribe?: () => void;
}

function Navbar({ onSubscribe }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = navLinks.map((l) => l.href.replace("#", ""));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const linkClass = (key: string) =>
    `whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
      active === key
        ? "bg-primary-500/15 text-primary-300"
        : "text-foreground-300 hover:text-foreground-50"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-800 bg-background-950/85 backdrop-blur-md"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="container-x flex h-16 items-center justify-between gap-4">
        <a href="#home" className="group flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-950">
            <i className="ri-moon-clear-line text-lg" aria-hidden="true"></i>
          </span>
          <h1 className="font-serif text-xl font-bold tracking-wide text-foreground-50">
            {brand.name}
          </h1>
        </a>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navLinks.map((l) => (
            <a key={l.key} href={l.href} className={linkClass(l.key)}>
              {l.name}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <a
            href="#subscribe"
            onClick={onSubscribe}
            className="whitespace-nowrap rounded-full border border-background-700 px-4 py-2 text-sm text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300"
          >
            <i className="ri-mail-add-line mr-1.5" aria-hidden="true"></i>
            订阅专题
          </a>
          <a
            href="#featured"
            className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
          >
            开始吃瓜
          </a>
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-background-800 text-foreground-100 md:hidden"
        >
          <i className={open ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} aria-hidden="true"></i>
        </button>
      </div>

      {open && (
        <div className="border-t border-background-800 bg-background-950/95 backdrop-blur-md md:hidden">
          <nav className="container-x flex flex-col gap-1 py-4" aria-label="移动端导航">
            {navLinks.map((l) => (
              <a
                key={l.key}
                href={l.href}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-lg px-3 py-3 text-sm text-foreground-100 transition-colors hover:bg-background-900"
              >
                {l.name}
                <i className="ri-arrow-right-line text-foreground-600" aria-hidden="true"></i>
              </a>
            ))}
            <a
              href="#subscribe"
              onClick={() => setOpen(false)}
              className="mt-2 flex items-center justify-center whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-sm font-medium text-background-950"
            >
              订阅深度专题
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Navbar;